import { drypApi } from "@/services/drypApi";
import { lensApi } from "@/services/lensApi";
import { unit } from "./unit";
import { structure } from "./structure";
import { DateTime } from "luxon";
import { getStructureFilterParamsForRequst, StructureFilterParams } from "@/utils/filters";

export interface Event {
	DurationInSeconds: number;
	End?: string;
	IsEventDataValidated: boolean;
	PointId: Dryp.StructureId;
	Start: string;
	Statistics: Statistic[];
}

export interface EventWithCalculations extends Event {
	rainAccumulation: {
		value: number;
		unit: string;
	};
	overflow: {
		value: number;
		unit: string;
	};
}

export interface Statistic {
	ParameterId: number;
	StatisticFunctionName: string;
	UnitId: Dryp.UnitId;
	Value: number;
}

export type EventsResponse = Event[];
export interface GetAllEventsRequest {
	from: string;
	to: string;
}

export interface StructureEvents {
	structure: Dryp.Structure;
	events: EventWithCalculations[];
	lastEventDate?: string;
	overflow: {
		value: number;
		unit: string;
	};
	rainAccumulation: {
		value: number;
		unit: string;
	};
}

let units: Dryp.UnitInfo[] = [];
let structures: Dryp.Structure[] = [];

const RAIN_ACCUMULATION_ID = 310;
const OVERFLOW_VOLUME_ID = 1005;

export interface EventType {
	id: number;
	name: string;
}

/** Mirrors the backend EventTypeIdAndName enum: OverFlow = 1, Rain = 2, Blockage = 3 */
export type EventTypeId = 1 | 2 | 3;

export interface EventListItem {
	structureId: number;
	structureName: string;
	eventType: EventType;
	start: number;
	end: number;
	durationInSeconds: number;
	isEventDataValidated: boolean;
	isActive: boolean;
}

export type EventListResponse = Dryp.ApiList<EventListItem>;

export type EventStatisticsGranularity = "Hour" | "Day" | "Month" | "Year";

export interface EventCountsByPeriod {
	periodStart: string;
	rainCount: number;
	overflowCount: number;
	blockageCount: number;
}

interface EventsStatisticsResponse {
	rainCount: number;
	overflowCount: number;
	blockageCount: number;
	rainInDays: number;
	byPeriod: EventCountsByPeriod[];
}

export const event = {
	getStatistics: async (
		from: string,
		to: string,
		structureId: number,
		granularity?: EventStatisticsGranularity,
	): Promise<EventsStatisticsResponse | null> => {
		try {
			const response = await lensApi.get<EventsStatisticsResponse>(`/events/statistics/${structureId}`, {
				params: {
					from,
					to,
					...(granularity ? { granularity } : {}),
				},
			});
			return response;
		} catch (error) {
			throw error;
		}
	},
	getEvents: async (
		pagination: Dryp.PaginationQuery,
		sort?: Dryp.SortQuery,
		structureFilters?: StructureFilterParams,
		eventTypes?: number[],
		from?: string,
		to?: string,
	): Promise<EventListResponse | null> => {
		try {
			const sortParams = sort
				? {
						direction: sort.direction,
						sort: sort.sort,
					}
				: {};
			const response = await lensApi.get<EventListResponse>(`/events`, {
				params: {
					...pagination,
					...sortParams,
					...getStructureFilterParamsForRequst(structureFilters || {}),
					...(eventTypes && eventTypes.length > 0 ? { "filter[eventTypes]": eventTypes } : {}),
					from,
					to,
				},
			});
			return response;
		} catch (error) {
			throw error;
		}
	},
	getAllEvents: async (dateRange: GetAllEventsRequest): Promise<StructureEvents[]> => {
		const eventsResponse = await drypApi.get<EventsResponse>(`/api/events`, {
			params: { From: dateRange.from, To: dateRange.to },
		});

		const unitsResponse = units.length > 0 ? units : await unit.getAllUnits();
		units = unitsResponse;
		if (structures.length === 0) {
			const structuresResponse = await structure.getAllStructures({ pageNumber: 0, pageSize: 2000 });
			structures = structuresResponse?.items || [];
		}

		const groupedEvents = structures
			.map(structure => {
				const events = eventsResponse
					.filter(({ PointId }) => PointId === structure.id)
					.sort((a, b) => {
						const aDate = a.End ? DateTime.fromISO(a.End) : DateTime.invalid("no End");
						const bDate = b.End ? DateTime.fromISO(b.End) : DateTime.invalid("no End");
						return bDate.toMillis() - aDate.toMillis();
					})
					.map(event => {
						const rainStatistic = event.Statistics.find(
							({ ParameterId }) => ParameterId === RAIN_ACCUMULATION_ID,
						);
						const overflowStatistic = event.Statistics.find(
							({ ParameterId }) => ParameterId === OVERFLOW_VOLUME_ID,
						);
						const rainUnit = units.find(({ IdUnits }) => IdUnits === rainStatistic?.UnitId)?.Name;
						const overflowUnit = units.find(({ IdUnits }) => IdUnits === overflowStatistic?.UnitId)?.Name;
						return {
							...event,
							rainAccumulation: {
								value: rainStatistic?.Value || 0,
								unit: rainUnit || "",
							},
							overflow: {
								value: overflowStatistic?.Value || 0,
								unit: overflowUnit || "",
							},
						};
					});
				const totalRain = events.reduce((sum, e) => sum + e.rainAccumulation.value, 0);
				const totalOverflow = events.reduce((sum, e) => sum + e.overflow.value, 0);

				const rainUnit = events?.[0]?.rainAccumulation?.unit;
				const overflowUnit = events?.[0]?.overflow?.unit;
				return {
					structure,
					events,
					lastEventDate: events[0]?.End || undefined,
					rainAccumulation: {
						value: totalRain,
						unit: rainUnit,
					},
					overflow: {
						value: totalOverflow,
						unit: overflowUnit,
					},
				};
			})
			.filter(({ events }) => events.length > 0)
			.sort((a, b) => b.events.length - a.events.length);
		console.log("groupedEvents", groupedEvents);
		return groupedEvents;
	},
};
